// Story 7.11 (ADO-1142), AC1/AC2 — the grid Substitutes panel (Figma 760:6045 /
// component 760:6082). An in-card overlay (NOT the full-screen DS Modal): it
// overlays the bottom of the out-of-stock ProductCard with up to 3 alternative
// products. Each row = thumbnail (ProductImage, reused from Story 7.4) + name
// (with the pack size embedded) + price + an Add button that reuses the Story 7.9
// one-click basket add (`useBasketStore`, per-SKU optimistic + mutex).
//
// PRICE (ADO-1142 lists it as a required row attribute): the row shows the substitute's
// unit price through the shared `ProductPrice`, the same figure the ProductCard footer
// shows (Figma 4002:18795 "Price Row"). With real prices on (Pricing migration 17,
// ADO-2248) the panel asks for its substitutes' prices itself, in one request
// and only while it is open: both hosts (the orderable card and the not-available search
// card) mount it closed. Pricing migration 17b (ADO-3948, DEC-3) moved both hosts' ask to the
// host itself, which prices its candidates when they load so it can leave out one with no price
// of its own before it offers a substitute (`pricedByHost`). Pack
// size is embedded in the name rather than shown as its own field (ADO requires it
// displayed, not as a separate element; customer names usually already carry it) so
// the meta slot is free for the price (2026-07-20 feedback).
//
// a11y (NFR24–26, Story 7.7 44px floor): role="dialog", labelled by its heading,
// Escape closes and the trigger regains focus (handled by the parent card),
// close button focused on open. It is a NON-modal popover (aria-modal is not
// set) — an inline affordance on one card in a grid, not a page-blocking modal —
// so it does not trap Tab or lock scroll; Escape + the Close button dismiss it.

import { useEffect, useRef, type FocusEvent, type KeyboardEvent } from 'react';
import { Button, Icon } from '../../design-system';
import { useBasketStore } from '../../basket/useBasketStore';
import { useAddConfirmation } from '../../basket/useAddConfirmation';
import type { SubstituteProduct } from '../../api/substitutes.client';
import { ProductImage } from './ProductImage';
import { ProductPrice, usePriceBlocksAdd } from '../../pricing/ProductPrice';
import { usePrices } from '../../pricing/usePriceStore';
import styles from './SubstitutesPanel.module.css';

/** The displayed name with its pack size embedded (ADO-1142 requires the pack
 *  size shown; the price now owns the meta slot). Customer names usually already
 *  carry the size, so the pack size is appended ONLY when the name does not
 *  already contain it — avoiding "Munchies 52g 52G" duplication. Whitespace is
 *  ignored in the check so "100G 24" matches a name ending "100g". */
function nameWithPackSize(name: string, packSize: string | null): string {
  if (packSize === null || packSize.trim() === '') return name;
  const squash = (s: string) => s.toLowerCase().replace(/\s+/g, '');
  return squash(name).includes(squash(packSize)) ? name : `${name} ${packSize.trim()}`;
}

export interface SubstitutesPanelProps {
  /** The out-of-stock product these are substitutes for — names the dialog (a11y). */
  readonly productName: string;
  /** The suggestions to show — the ProductCard has already OOS-excluded them from
   *  the availability store and capped the list at 3 (Story 7-FU-I, ADO-1222; the
   *  7.11 "excluded upstream" invariant became real there). */
  readonly substitutes: readonly SubstituteProduct[];
  /** Whether the sheet is open. The panel stays MOUNTED either way so the
   *  prototype's 400ms transitions can run (2026-07-17 parity). */
  readonly open: boolean;
  /** Prototype variants: `overlay` = the slide-up sheet over the card bottom
   *  (grid card, and the list card <768px); `inline` = the expand/collapse
   *  block UNDER the card content (list card ≥768px, prototype
   *  `substituteInlineBlock` — grid-template-rows 0fr→1fr transition). */
  readonly variant?: 'overlay' | 'inline';
  /** Dismiss the panel (the parent card returns focus to the trigger). */
  readonly onClose: () => void;
  /** Pricing migration 17b (ADO-3948, DEC-3): the host has already asked for these prices, as
   *  both cards do when their substitutes load, so opening the panel asks nothing again. Defaults
   *  to `false`: the panel asks for its rows' prices when it opens. */
  readonly pricedByHost?: boolean;
  /** Story 7.12 Iteration 2 (ADO-911) — suppress the Linked Product upsell for
   *  adds made from this panel. Set by a card rendered INSIDE the upsell modal,
   *  so a substitute add cannot stack a second modal on the first (or, worse,
   *  swap the open dialog's cards and badge underneath the user). Mirrors
   *  `ProductCard`'s prop of the same name. Defaults to `false`. */
  readonly suppressUpsell?: boolean;
}

export function SubstitutesPanel({
  productName,
  substitutes,
  open,
  variant = 'overlay',
  onClose,
  suppressUpsell = false,
  pricedByHost = false,
}: SubstitutesPanelProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  usePrices(open && !pricedByHost ? substitutes.map((substitute) => substitute.sku) : []);

  // Move focus into the panel on open so keyboard users land on it (WAI-ARIA
  // dialog pattern); the parent restores focus to the trigger on close.
  // preventScroll: the sheet is still animating in when this runs, and a plain
  // focus() makes the browser scroll the card's overflow-hidden box to reveal
  // it — an abrupt visual jump over the transition. When two variants are
  // mounted (list card), the media-hidden one is display:none, so its focus()
  // no-ops and the visible variant keeps focus.
  useEffect(() => {
    if (open) closeRef.current?.focus({ preventScroll: true });
  }, [open]);

  // Story 7-FU-I (ADO-1222) - RECOVER focus when a live availability push takes away the row
  // the user is standing on (OQ-4: pushes flip a substitute out of an open panel).
  //
  // The card already covers the case where the LAST substitute goes and the panel closes with
  // it (`closeSubstitutes` lands on the trigger, or the card title if the CTA went too). This
  // is the other case, which nothing covered: some substitutes remain, so the panel stays
  // OPEN, and only the focused row unmounts, leaving a keyboard user outside the panel with no
  // focus ring and Escape no longer reaching the dialog (round 15, F6).
  //
  // Recovery goes to the panel's own Close button: it is the panel's designated entry point
  // (the same place `open` puts focus) and it exists for as long as the panel does, unlike any
  // row. Two conditions have to hold TOGETHER - the element the panel last saw focus on must be
  // gone from the document, and focus must not be sitting on anything focusable now. Note what
  // the second one does NOT exclude: a click on non-focusable page content leaves
  // `document.activeElement` at `<body>`, which passes. So it alone cannot tell "the row under
  // the user's focus just vanished" from "the user left, and something else vanished later" -
  // that is why the record is DROPPED as soon as it is found stale, below, and the pair is only
  // ever consulted on the commit that removed the element it names.
  //
  // The two conditions are read from `isConnected` and `document.activeElement`, never from a
  // blur event: whether removing the focused element fires focusout at all is exactly the kind
  // of runtime detail that differs between engines, and neither guard needs an answer to it.
  //
  // Story 7-FU-K (ADO-1528, AC7) gave the record a DEPARTURE release, so a claim is spent when the
  // user leaves and not only when its node is removed. It is read TWO ways from one rule
  // (`departed`, below): promptly from the delegated `focusout` (`onPanelBlur`), and by SAMPLING
  // `document.activeElement` on every commit. Both are needed, and each covers the other's gap —
  // see `onPanelBlur` for which ordering each one catches. What they share is the rule: focus has
  // left only when it is on something FOCUSABLE OUTSIDE the panel, never merely because nothing
  // holds it.
  const lastFocusedInPanel = useRef<Element | null>(null);
  // Keyed by CONTENT via `JSON.stringify`, not `join`, so a SKU containing the delimiter can't
  // collide two different sets into one key (`['A,B']` vs `['A','B']`) and hide the change this
  // effect exists to detect. Same rule as `useCandidateAvailability`'s `candidateKey`.
  const renderedSkus = JSON.stringify(substitutes.map((s) => s.sku));
  const previousSkus = useRef(renderedSkus);
  // The panel root the record was made in — the element carrying `onFocus`/`onBlur`, taken from
  // the delegated handler's own `currentTarget`. `departed` needs a root to test containment
  // against, and this is the root focus was actually seen in.
  //
  // A `useRef` on the rendered root would do the same job: each `inline` branch returns exactly
  // ONE root, and the list card's two variants are two separate `SubstitutesPanel` INSTANCES
  // (`ProductCard.tsx:569` and `:585`), so a ref could only ever name its own instance's root.
  // `currentTarget` is used because the handler already has it, not to solve a cross-variant
  // problem — there isn't one.
  const panelOfRecord = useRef<Element | null>(null);

  // THE DEPARTURE RULE, in one place because it is read two ways (an event and a sample) and they
  // must not drift: focus has left the panel only when it is on something FOCUSABLE OUTSIDE it.
  //
  // `<body>` is deliberately NOT a departure. It is what the document reports whenever nothing
  // focusable holds focus, which covers a click on the panel's own non-focusable content and the
  // focused Add button becoming `disabled` mid-request — a user who has not gone anywhere. It is
  // indistinguishable from "the user left and clicked dead space", and of the two readings the
  // costly one is dropping the claim: that strands a keyboard user on `<body>` when the row is
  // then taken away, deleting the round-15 F6 recovery this panel exists to provide. Keeping it
  // only risks a focus jump to Close, which is inside the panel the user was last in. So the test
  // is three-way rather than `!contains(activeElement)`.
  const departed = (target: Element | null): boolean =>
    target !== null &&
    target !== document.body &&
    !(panelOfRecord.current?.contains(target) ?? false);

  useEffect(() => {
    const changed = previousSkus.current !== renderedSkus;
    previousSkus.current = renderedSkus;
    if (!open) return;

    // DEPARTURE, SAMPLED — the second of the two readings of one rule (see `departed` below for
    // the rule itself and `onPanelBlur` for the first). Runs on every commit, before the removal
    // check and regardless of whether the rendered set changed.
    //
    // It exists because `onPanelBlur` can only act on a `focusout` the panel is actually told
    // about, and focus can leave an element without one — the focused Add button becoming
    // `disabled` mid-request is the case this component itself contains (`disabled={pending}`), and
    // whether an engine fires `focusout` for that is exactly the sort of detail the two conditions
    // below were written to avoid depending on. Sampling asks the DOM where focus IS instead of
    // trusting that it was told, so a missed event costs promptness and not correctness.
    //
    // NOT INDEPENDENTLY PINNED, stated rather than implied: happy-dom dispatches `focusout` for
    // every programmatic focus change, so `onPanelBlur` reaches first in every ordering a unit test
    // can build and reverting this block alone turns no test red. Confirming it needs a real engine
    // that drops focus without an event (Playwright), which is recorded as a follow-up in
    // `deferred-work.md`. Kept meanwhile because the hazard it covers is documented behaviour and
    // the alternative is a claim that survives the user leaving — this theme's original defect.
    const recorded = lastFocusedInPanel.current;
    if (recorded !== null && recorded.isConnected && recorded !== document.activeElement) {
      if (departed(document.activeElement)) {
        lastFocusedInPanel.current = null;
        return;
      }
    }

    if (!changed) return;
    const orphaned = lastFocusedInPanel.current;
    if (orphaned === null || orphaned.isConnected) return; // nothing of ours was removed
    // Our record names a removed node, so it is spent either way: we are about to recover from
    // it, or to decline because the user is somewhere else. Dropping it here is what keeps the
    // decline from lingering as a stale claim that a later removal could act on.
    lastFocusedInPanel.current = null;
    // `<body>` is what a rendered document reports when nothing focusable holds focus, so that
    // is the state to look for.
    if (document.activeElement !== document.body) return; // focus is somewhere real
    closeRef.current?.focus({ preventScroll: true });
    // NO dependency array, deliberately: the departure above is sampled rather than delivered, so
    // it has to be able to run on a commit that changed neither `open` nor the rendered set — a
    // tab out of the panel renders nothing by itself, and the commit that notices it is whichever
    // one comes next. The removal half is still gated on `changed`, so it behaves exactly as it
    // did when the deps were `[open, renderedSkus]`.
  });

  // React's onFocus is the DELEGATED focusin, so this fires for focus landing anywhere inside
  // the panel — including a tab between rows, which no re-render accompanies. Recording on a
  // commit instead would miss exactly that: the user moves onto a row, nothing renders, and the
  // push then arrives with the panel still believing focus sat where it did at mount.
  //
  // This only ever names an element that HELD focus inside the panel, and it is cleared by the
  // effect above the moment that element leaves the document — so it is either live (its node is
  // still connected, and `isConnected` says so) or gone within one commit of becoming stale. A
  // recovery re-records immediately: `focus()` dispatches focusin, which this same handler sees.
  const onPanelFocus = (e: FocusEvent<HTMLDivElement>) => {
    lastFocusedInPanel.current = e.target;
    panelOfRecord.current = e.currentTarget;
  };

  // DEPARTURE, EVENT-DRIVEN — the PROMPT reading of `departed`. React's `onBlur` is the delegated
  // `focusout`, which (unlike native `blur`) BUBBLES, so one handler on the element already
  // carrying `onFocus` sees focus leaving any row.
  //
  // Why BOTH this and the sample, rather than either alone. This one is prompt but conditional on
  // the event arriving; the sample is unconditional but only runs on the next commit. Neither
  // covers the other's gap: without this handler, "focus a row → tab out → click dead space → a
  // push removes the recorded row" steals focus, because no commit happens between the tab-out and
  // the removal for a sample to run in. Without the sample, a focus loss that fires no `focusout`
  // (the disabled-button case) leaves the claim live and the same ordering steals focus again. Two
  // readings of one rule, and `departed` is that rule so they cannot disagree.
  //
  // `relatedTarget` is where focus went — and it is `null` when focus goes nowhere, which
  // `departed` deliberately declines. That `null` was the bug in the first version of this handler:
  // it fell through and spent the claim, so clicking the panel's own text (or an Add button
  // disabling itself) silently deleted the recovery.
  const onPanelBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!departed(e.relatedTarget)) return;
    const recorded = lastFocusedInPanel.current;
    // A "departure" whose node has already gone is a REMOVAL, and removals belong to the recovery
    // effect. Whether an engine reports `focusout` for a removed element therefore does not matter:
    // if it does, this declines; if it does not, the record survives to the effect either way.
    if (recorded === null || !recorded.isConnected) return;
    lastFocusedInPanel.current = null;
  };

  const inline = variant === 'inline';
  const onEscape = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  };

  const content = (
    <>
      <div className={inline ? styles.inlineHeader : styles.header}>
        <h4 className={styles.title}>Substitutes</h4>
        <button
          type="button"
          ref={closeRef}
          className={styles.close}
          aria-label={inline ? 'Collapse substitutes' : 'Close substitutes'}
          onClick={onClose}
          tabIndex={open ? 0 : -1}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      <ul className={styles.list}>
        {substitutes.map((substitute) => (
          <li
            key={substitute.sku}
            className={inline ? `${styles.row} ${styles.rowInline}` : styles.row}
          >
            <div className={styles.thumb}>
              <ProductImage src={substitute.imageUrl} alt={substitute.name} />
            </div>
            {/* Content column: the full (wrapping) name on top, then a footer row
                carrying the meta on the left and the Add button on the right —
                so a long name never squeezes the meta/CTA (2026-07-20 feedback). */}
            <div className={styles.content}>
              <p className={styles.name}>
                {nameWithPackSize(substitute.name, substitute.packSize)}
              </p>
              <div className={styles.footer}>
                {/* PRICE: the design's row slot (see file header). */}
                <ProductPrice
                  sku={substitute.sku}
                  productName={substitute.name}
                  as="p"
                  className={styles.meta}
                />
                <SubstituteAddButton
                  sku={substitute.sku}
                  name={substitute.name}
                  open={open}
                  suppressUpsell={suppressUpsell}
                />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );

  if (inline) {
    // Prototype `substituteInlineBlock`: an under-card region that expands via
    // a grid-template-rows 0fr→1fr transition (no overlay, no translate).
    return (
      <div
        className={`${styles.inlineWrap} ${open ? styles.inlineWrapOpen : ''}`}
        aria-hidden={!open}
        onFocus={onPanelFocus}
        onBlur={onPanelBlur}
      >
        <div className={styles.inlineInner}>
          {/* Same Escape-to-close affordance as the overlay dialog. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            role="region"
            aria-label={`Substitutes for ${productName}`}
            className={styles.inlineBlock}
            onKeyDown={onEscape}
          >
            {content}
          </div>
        </div>
      </div>
    );
  }

  return (
    // The onKeyDown implements Escape-to-close (WAI-ARIA dialog pattern) on the
    // role="dialog" container — the intended structure, not a noninteractive misuse.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      role="dialog"
      aria-label={`Substitutes for ${productName}`}
      aria-hidden={!open}
      className={`${styles.panel} ${open ? styles.panelOpen : ''}`}
      onKeyDown={onEscape}
      onFocus={onPanelFocus}
      onBlur={onPanelBlur}
    >
      {content}
    </div>
  );
}

/**
 * The per-substitute Add button — reuses the Story 7.9 `useBasketStore` one-click
 * add exactly as the main ProductCard does (per-SKU optimistic + pending mutex +
 * reconcile). Kept as its own component so each row subscribes to only its OWN
 * SKU's pending/error slice (no cross-row re-render). The store's AC3 gate still
 * blocks a supplier-UNAVAILABLE substitute defence-in-depth, on top of the
 * ProductCard's client-side OOS-exclusion of substitutes (OQ-5, D-10; made real in
 * Story 7-FU-I, ADO-1222).
 */
function SubstituteAddButton({
  sku,
  name,
  open,
  suppressUpsell,
}: {
  readonly sku: string;
  readonly name: string;
  readonly open: boolean;
  readonly suppressUpsell: boolean;
}) {
  const pending = useBasketStore((s) => s.pending.has(sku));
  // ADO-2248 PS-2: the Add waits for the substitute's price, with main's disabled look.
  const priceBlocksAdd = usePriceBlocksAdd(sku);
  const error = useBasketStore((s) => s.mutationErrors.get(sku) ?? null);
  // AC2 (PR #106 review) — a transient inline "Added ✓" after a successful add so
  // the user gets success feedback (there is no basket screen yet). Story 7-FU-D
  // (ADO-1217), AC5: the shared, unmount-guarded confirm hook (identical to the
  // ProductCard inline add and the RecommendationRail tile) owns that logic + the
  // mounted guard so a substitute added as the panel closes never updates a dead row.
  // Story 7.12 Iteration 2 (AC7) — a substitute add is an add-to-basket entry
  // point too, so it carries the name and fires the Linked Product modal —
  // EXCEPT when this panel belongs to a card rendered inside that modal, where
  // firing again would swap the open dialog's cards and badge underneath the user.
  // ADO-911 design refresh — the substitutes panel is a listing-context surface,
  // so a substitute add opens the non-invasive TOAST (never the PDP modal).
  const { confirmed, confirm } = useAddConfirmation(sku, {
    productName: name,
    upsellVariant: 'toast',
    upsell: !suppressUpsell,
  });

  return (
    <div className={styles.add}>
      <Button
        variant="primary"
        className={styles.addButton}
        onClick={() => void confirm()}
        disabled={pending || priceBlocksAdd}
        aria-label={`Add substitute ${name} to basket`}
        tabIndex={open ? 0 : -1}
      >
        {confirmed ? 'Added ✓' : 'Add'}
      </Button>
      {/* AC2 (a11y) — the button's static aria-label wins over its text, so the
          visual "Added ✓" is silent to assistive tech. This polite live region
          announces the confirmation without disturbing the button's disambiguating
          name. Present-but-empty until an add lands. */}
      <span className={styles.visuallyHidden} role="status">
        {confirmed ? `Added substitute ${name} to basket` : ''}
      </span>
      {error && (
        <p className={styles.addError} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
